/**
 * Normalizes API-provided comment type colors for UI usage.
 *
 * Returns `null` when the value should behave like "no explicit color" (theme default),
 * including legacy sentinel values that used to mean "white/default".
 */
export function normalizeCommentTypeColor(color: string | null | undefined): string | null {
  if (color == null) {
    return null;
  }

  const trimmed = color.trim();
  if (trimmed.length === 0) {
    return null;
  }

  if (isDefaultCommentTypeColorValue(trimmed)) {
    return null;
  }

  return trimmed;
}

export function isDefaultCommentTypeColor(color: string | null | undefined): boolean {
  return normalizeCommentTypeColor(color) === null;
}

function isDefaultCommentTypeColorValue(trimmedColor: string): boolean {
  const lowered = trimmedColor.toLowerCase();
  if (lowered === 'white') {
    return true;
  }

  if (lowered.startsWith('#')) {
    return isDefaultHexColor(lowered);
  }

  const rgbMatch = /^rgba?\(\s*([^)]+)\s*\)$/i.exec(trimmedColor);
  if (rgbMatch) {
    return isDefaultRgbColorString(rgbMatch[1]);
  }

  return false;
}

function isDefaultHexColor(hex: string): boolean {
  const value = hex.startsWith('#') ? hex.slice(1) : hex;
  if (!/^[0-9a-f]+$/i.test(value)) {
    return false;
  }

  if (value.length === 3) {
    return value.toLowerCase() === 'fff';
  }

  if (value.length === 6) {
    return value.toLowerCase() === 'ffffff';
  }

  if (value.length === 8) {
    const rgb = value.slice(0, 6).toLowerCase();
    const aa = value.slice(6, 8).toLowerCase();
    return rgb === 'ffffff' && aa === 'ff';
  }

  return false;
}

function isDefaultRgbColorString(body: string): boolean {
  const parts = body
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0);

  if (parts.length < 3 || parts.length > 4) {
    return false;
  }

  const r = Number(parts[0]);
  const g = Number(parts[1]);
  const b = Number(parts[2]);
  if (![r, g, b].every(component => Number.isFinite(component))) {
    return false;
  }

  if (r !== 255 || g !== 255 || b !== 255) {
    return false;
  }

  if (parts.length === 3) {
    return true;
  }

  const a = Number(parts[3]);
  if (!Number.isFinite(a)) {
    return false;
  }

  return Math.abs(a - 1) < 1e-6 || a === 0;
}
